/**
 * BusinessTypePicker — the icon-grid business-category selector shared between
 * the onboarding "business details" slide-over and the settings Company Info
 * panel, so both stay in sync.
 *
 * The picker owns the canonical BUSINESS_TYPES list (key → icon → gradient →
 * Salunas eligibility) and renders two labelled groups (Salunas-eligible beauty
 * sector first, then other types). It is presentation-only: callers pass the
 * selected key, an onSelect handler, and a translate function `t` (i18n lives in
 * the consuming app). Labels resolve from `activation.slideOver.business.type.*`.
 */
import { motion } from 'framer-motion';
import {
  Scissors,
  Sparkles,
  Dumbbell,
  PawPrint,
  Wrench,
  GraduationCap,
  MoreHorizontal,
  Heart,
  Zap,
  Waves,
  Pen,
  CheckCircle2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

function cn(...inputs: (string | boolean | undefined | null)[]) {
  return inputs.filter(Boolean).join(' ');
}

// ─── Business type configuration ─────────────────────────────────────────────

export interface BusinessTypeOption {
  key: string;
  labelKey: string;
  icon: LucideIcon;
  /** Gradient classes for the selected state. */
  gradient: string;
  /** Whether this maps to the Salunas (beauty) marketplace. */
  salunas: boolean;
}

/**
 * Ordered list of selectable business categories. Salunas-eligible types are
 * grouped first so the visual separation is clear to the user.
 */
export const BUSINESS_TYPES: BusinessTypeOption[] = [
  // ── Salunas-eligible (beauty sector) ───────────────────────────────────
  { key: 'hair',    labelKey: 'activation.slideOver.business.type.hair',        icon: Scissors,     gradient: 'from-pink-500 to-rose-500',    salunas: true },
  { key: 'barber',  labelKey: 'activation.slideOver.business.type.barber',      icon: Scissors,     gradient: 'from-slate-500 to-zinc-500',   salunas: true },
  { key: 'nails',   labelKey: 'activation.slideOver.business.type.nails',       icon: Sparkles,     gradient: 'from-fuchsia-500 to-pink-500', salunas: true },
  { key: 'beauty',  labelKey: 'activation.slideOver.business.type.beauty',      icon: Heart,        gradient: 'from-rose-500 to-red-500',     salunas: true },
  { key: 'massage', labelKey: 'activation.slideOver.business.type.massage',     icon: Waves,        gradient: 'from-cyan-500 to-teal-500',    salunas: true },
  { key: 'spa',     labelKey: 'activation.slideOver.business.type.spa',         icon: Zap,          gradient: 'from-violet-500 to-purple-500',salunas: true },
  { key: 'tattoo',  labelKey: 'activation.slideOver.business.type.tattoo',      icon: Pen,          gradient: 'from-gray-700 to-gray-900',    salunas: true },
  // ── Other business types ────────────────────────────────────────────────
  { key: 'fitness', labelKey: 'activation.slideOver.business.type.fitness',     icon: Dumbbell,     gradient: 'from-orange-500 to-amber-500', salunas: false },
  { key: 'pets',    labelKey: 'activation.slideOver.business.type.pets',        icon: PawPrint,     gradient: 'from-lime-500 to-green-500',   salunas: false },
  { key: 'home_auto', labelKey: 'activation.slideOver.business.type.homeAuto',  icon: Wrench,       gradient: 'from-blue-500 to-indigo-500',  salunas: false },
  { key: 'professional', labelKey: 'activation.slideOver.business.type.professional', icon: GraduationCap, gradient: 'from-sky-500 to-blue-500', salunas: false },
  { key: 'other',   labelKey: 'activation.slideOver.business.type.other',       icon: MoreHorizontal, gradient: 'from-stone-400 to-stone-600', salunas: false },
];

/** Keys that map a tenant to the Salunas (beauty) marketplace. */
export const SALUNAS_BUSINESS_TYPE_KEYS = new Set(
  BUSINESS_TYPES.filter((b) => b.salunas).map((b) => b.key)
);

// ─── Props ────────────────────────────────────────────────────────────────────

export interface BusinessTypePickerProps {
  /** Currently selected business-type key (empty string when none). */
  value: string;
  /** Called with the chosen key. */
  onChange: (key: string) => void;
  /** i18n translate function — labels resolve from activation.slideOver.business.* */
  t: (key: string) => string;
  /** id used to wire aria-labelledby on the option groups. */
  legendId?: string;
}

// ─── BusinessTypePicker ───────────────────────────────────────────────────────

export function BusinessTypePicker({
  value,
  onChange,
  t,
  legendId = 'business-type-legend',
}: BusinessTypePickerProps) {
  const salunasTypes = BUSINESS_TYPES.filter((b) => b.salunas);
  const otherTypes = BUSINESS_TYPES.filter((b) => !b.salunas);

  return (
    <div className="space-y-3">
      {/* Salunas-eligible group */}
      <div>
        <p className="text-xs text-muted-foreground mb-2 font-medium uppercase tracking-wide">
          {t('activation.slideOver.business.typeSalunasGroup')}
        </p>
        <div className="grid grid-cols-4 gap-2" role="group" aria-labelledby={legendId}>
          {salunasTypes.map((biz) => (
            <BusinessTypeCard
              key={biz.key}
              option={biz}
              selected={value === biz.key}
              onSelect={() => onChange(biz.key)}
              t={t}
            />
          ))}
        </div>
      </div>

      {/* Other business types group */}
      <div>
        <p className="text-xs text-muted-foreground mb-2 font-medium uppercase tracking-wide">
          {t('activation.slideOver.business.typeOtherGroup')}
        </p>
        <div className="grid grid-cols-4 gap-2">
          {otherTypes.map((biz) => (
            <BusinessTypeCard
              key={biz.key}
              option={biz}
              selected={value === biz.key}
              onSelect={() => onChange(biz.key)}
              t={t}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── BusinessTypeCard sub-component ──────────────────────────────────────────

interface BusinessTypeCardProps {
  option: BusinessTypeOption;
  selected: boolean;
  onSelect: () => void;
  t: (key: string) => string;
}

function BusinessTypeCard({ option, selected, onSelect, t }: BusinessTypeCardProps) {
  const Icon = option.icon;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={t(option.labelKey)}
      className={cn(
        'relative flex flex-col items-center justify-center gap-1.5',
        'rounded-xl border p-2.5 text-center transition-all duration-150 cursor-pointer',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        selected
          ? cn('border-transparent shadow-md', 'bg-gradient-to-br', option.gradient, 'text-white')
          : 'border-border bg-card text-muted-foreground hover:border-indigo-200 dark:hover:border-indigo-800/50 hover:bg-accent hover:text-foreground'
      )}
    >
      <Icon className={cn('w-5 h-5', selected ? 'text-white' : '')} aria-hidden="true" />
      <span className="text-[10px] font-medium leading-tight">{t(option.labelKey)}</span>
      {selected && (
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute top-1 right-1 w-3 h-3 bg-white/30 rounded-full flex items-center justify-center"
          aria-hidden="true"
        >
          <CheckCircle2 className="w-2.5 h-2.5 text-white" />
        </motion.span>
      )}
    </button>
  );
}
